/**
 * Formats a phone number from E.164 format ("+15554443333") to North American format ("(555) 444-3333")
 * @param phone Phone number in E.164 format
 * @returns Formatted phone number or original string if invalid format
 */
export function formatPhoneNumber(phone: string): string {
    // Remove any non-digit characters and ensure we're working with a clean string
    const cleaned = phone.replace(/\D/g, '');

    // Check if we have a valid North American number (10 or 11 digits, if 11 must start with 1)
    const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/);

    if (match) {
        // Return formatted string using the captured groups
        return `(${match[1]}) ${match[2]}-${match[3]}`;
    }

    // Return original string if it doesn't match expected format
    return phone;
}

/**
 * Normalizes a raw phone string to E.164 format (+1XXXXXXXXXX).
 * Accepts any common North American format (dashes, spaces, dots, parens, +1 prefix, etc).
 * Returns null if the digit count is not 10 or 11 (with leading 1).
 */
export function normalizeToE164(raw: string): string | null {
    const digits = raw.replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('1')) {
        return '+' + digits
    }
    if (digits.length === 10) {
        return '+1' + digits
    }
    return null
}

/**
 * Formats a raw phone value for display in an input field with a +1 prefix.
 * Returns the raw value unchanged if it can't be normalized to 10 digits.
 */
export function formatPhoneForInput(raw: string): string {
    const digits = raw.replace(/\D/g, '')
    const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
    if (ten.length === 10) {
        return `+1 (${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
    }
    return raw
}
